'use client';

import {
  Autocomplete,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FileFormat, IngestionSourceType, Provider } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { Regions } from '@/data/inventory/regions';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';

interface RegionOption {
  value: string;
  label: string;
}

const AWS_REGION_OPTIONS: RegionOption[] = Object.entries(
  Regions[Provider.Aws]
).map(([code, { displayName }]) => ({
  value: code,
  label: `${code} - ${displayName}`,
}));

const GCS_LOCATION_OPTIONS: RegionOption[] = Object.entries(
  Regions[Provider.Gcp]
).map(([code, { displayName }]) => ({
  value: code,
  label: `${code} - ${displayName}`,
}));

const BUCKET_NAME_MAX_LENGTH = 63;

const BUCKET_NAME_VALIDATION: FieldValidation = {
  validateInput: (value: string) =>
    value.length === 0 || value.trim().length <= BUCKET_NAME_MAX_LENGTH,
  errorMessage: `Bucket name must be ${BUCKET_NAME_MAX_LENGTH} characters or fewer.`,
};

const {
  validation: ACCOUNT_ID_VALIDATION,
  placeholder: ACCOUNT_ID_PLACEHOLDER,
} = getCloudFieldConfig(Provider.Aws, 'accountId', { required: true });

export function SourceStep(props: StepProps<IngestionPipelineWizardState>) {
  const {
    sourceType,
    s3Bucket,
    s3Region,
    s3OwnerAccountId,
    gcsBucket,
    gcsProjectId,
    gcsLocation,
    fileFormat,
  } = props.wizardState;

  const isS3 = sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_S3;

  const selectedAwsRegion = useMemo(
    () => AWS_REGION_OPTIONS.find((o) => o.value === s3Region) ?? null,
    [s3Region]
  );
  const selectedGcsLocation = useMemo(
    () => GCS_LOCATION_OPTIONS.find((o) => o.value === gcsLocation) ?? null,
    [gcsLocation]
  );

  const handleSourceTypeChange = (newSourceType: IngestionSourceType) => {
    props.setWizardState((s) => ({
      ...s,
      sourceType: newSourceType,
      ...(newSourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_S3
        ? { gcsBucket: '', gcsProjectId: '', gcsLocation: '' }
        : { s3Bucket: '', s3Region: '', s3OwnerAccountId: '' }),
    }));
  };

  const canGoNext = () => {
    if (isS3) {
      return (
        s3Bucket.trim() !== '' &&
        s3Bucket.trim().length <= BUCKET_NAME_MAX_LENGTH &&
        s3Region.trim() !== '' &&
        ACCOUNT_ID_VALIDATION.validateInput(s3OwnerAccountId)
      );
    }
    return (
      gcsBucket.trim() !== '' &&
      gcsBucket.trim().length <= BUCKET_NAME_MAX_LENGTH &&
      gcsProjectId.trim() !== '' &&
      gcsLocation.trim() !== ''
    );
  };

  return (
    <StepContainer
      stepperIndex={1}
      stepperLabels={props.stepperLabels}
      canGoNext={canGoNext}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      onBackClick={props.back}
    >
      <Stack
        sx={{ height: '100%' }}
        alignItems='center'
        justifyContent='center'
      >
        <Stack width='500px' gap='24px'>
          {/* Source Type Selection */}
          <FormControl fullWidth>
            <FormLabel>Source Type</FormLabel>
            <Select
              size='small'
              value={sourceType}
              onChange={(e) =>
                handleSourceTypeChange(e.target.value as IngestionSourceType)
              }
              data-testid='source-type-select'
            >
              <MenuItem value={IngestionSourceType.INGESTION_SOURCE_TYPE_S3}>
                S3
              </MenuItem>
              <MenuItem value={IngestionSourceType.INGESTION_SOURCE_TYPE_GCS}>
                GCS
              </MenuItem>
            </Select>
          </FormControl>

          {/* S3 Source Properties */}
          {isS3 && (
            <>
              <Typography variant='subtitle2' color='text.secondary'>
                S3 Source Properties
              </Typography>

              <FormTextField
                title='Bucket'
                placeholder='e.g. my-bucket'
                value={s3Bucket}
                validation={BUCKET_NAME_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    s3Bucket: e.target.value,
                  }))
                }
                dataTestId='s3-bucket-input'
              />

              <FormControl fullWidth>
                <FormLabel>Region</FormLabel>
                <Autocomplete
                  options={AWS_REGION_OPTIONS}
                  value={selectedAwsRegion}
                  onChange={(_event, newValue) =>
                    props.setWizardState((s) => ({
                      ...s,
                      s3Region: newValue?.value ?? '',
                    }))
                  }
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      placeholder='Search regions…'
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': 's3-region-input',
                      }}
                    />
                  )}
                />
              </FormControl>

              <FormTextField
                title='Owner Account ID'
                placeholder={ACCOUNT_ID_PLACEHOLDER}
                value={s3OwnerAccountId}
                validation={ACCOUNT_ID_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    s3OwnerAccountId: e.target.value,
                  }))
                }
                dataTestId='s3-owner-account-input'
              />
            </>
          )}

          {/* GCS Source Properties */}
          {!isS3 && (
            <>
              <Typography variant='subtitle2' color='text.secondary'>
                GCS Source Properties
              </Typography>

              <FormTextField
                title='Bucket'
                placeholder='e.g. my-bucket'
                value={gcsBucket}
                validation={BUCKET_NAME_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    gcsBucket: e.target.value,
                  }))
                }
                dataTestId='gcs-bucket-input'
              />

              <FormControl fullWidth>
                <FormLabel>Project ID</FormLabel>
                <TextField
                  size='small'
                  placeholder='e.g. my-project-id'
                  value={gcsProjectId}
                  onChange={(e) =>
                    props.setWizardState((s) => ({
                      ...s,
                      gcsProjectId: e.target.value,
                    }))
                  }
                  data-testid='gcs-project-id-input'
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>Location</FormLabel>
                <Autocomplete
                  options={GCS_LOCATION_OPTIONS}
                  value={selectedGcsLocation}
                  onChange={(_event, newValue) =>
                    props.setWizardState((s) => ({
                      ...s,
                      gcsLocation: newValue?.value ?? '',
                    }))
                  }
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      placeholder='Search locations…'
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': 'gcs-location-input',
                      }}
                    />
                  )}
                />
              </FormControl>
            </>
          )}

          {/* Data Format */}
          <Typography variant='subtitle2' color='text.secondary'>
            Data Format
          </Typography>
          <FormControl fullWidth>
            <FormLabel>File Format</FormLabel>
            <Select
              size='small'
              value={fileFormat}
              onChange={(e) =>
                props.setWizardState((s) => ({
                  ...s,
                  fileFormat: e.target.value as FileFormat,
                }))
              }
              data-testid='file-format-select'
            >
              <MenuItem value={FileFormat.FILE_FORMAT_JSON}>JSON</MenuItem>
              <MenuItem value={FileFormat.FILE_FORMAT_AVRO}>AVRO</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    </StepContainer>
  );
}
